
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useApiTokens } from "@/hooks/useApiTokens";
import { OpenAIWrapper } from "@/services/openai/OpenAIWrapper";

export const useOpenAIKeywords = (brandId: string | null) => {
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { apiTokens } = useApiTokens(brandId);

  const generateKeywords = async (topic: string) => {
    if (!brandId) {
      toast.error("No brand selected");
      return;
    }

    if (!apiTokens.openai) {
      toast.error("OpenAI API key not configured. Please add it in API Settings.");
      return;
    }

    try {
      setIsLoading(true);
      console.log("Generating keywords for topic:", topic);

      // Get the SEO prompt from seo_prompts table
      const { data: promptData, error: promptError } = await supabase
        .from('seo_prompts')
        .select('prompt')
        .eq('brand_id', brandId)
        .eq('prompt_type', 'keyword_assistant')
        .single();

      if (promptError && promptError.code !== 'PGRST116') {
        throw promptError;
      }

      const defaultPrompt = `Generate a list of 20 relevant keywords for keyword research based on the following topic: {topic}

Please provide:
- High-volume search terms
- Long-tail keywords
- Commercial intent keywords
- Question-based keywords

Return only the keywords, one per line, without numbering or bullets.`;

      const prompt = promptData?.prompt || defaultPrompt;
      const finalPrompt = prompt.replace('{topic}', topic);

      console.log("Using prompt:", finalPrompt);

      // Call OpenAI API using wrapper
      const wrapper = new OpenAIWrapper();
      const response = await wrapper.chatCompletion({
        apiKey: apiTokens.openai,
        model: apiTokens.openai_model || 'gpt-4o-mini',
        messages: [{ role: 'user', content: finalPrompt }],
        source: 'keyword_generator',
        brandId: brandId!,
        temperature: 0.7,
        max_tokens: 1000
      });

      const generatedText = response.choices[0].message.content;

      // Parse keywords from the response
      const keywordList = generatedText
        .split('\n')
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0)
        .map((line: string) => line.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, ''))
        .filter((keyword: string) => keyword.length > 0);

      console.log("Generated keywords:", keywordList);
      setKeywords(keywordList);
      toast.success(`Generated ${keywordList.length} keywords`);
      return keywordList;

    } catch (error) {
      console.error("Error generating keywords:", error);
      toast.error("Failed to generate keywords. Please try again.");
      setKeywords([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  return {
    keywords,
    isLoading,
    generateKeywords,
    clearKeywords: () => setKeywords([])
  };
};
